import React, { Suspense, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { ToastProvider } from '../../contexts/ToastContext';
import {
  Box,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BarChartIcon from '@mui/icons-material/BarChart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useAuth } from '../../contexts/AuthContext';
import { UserMenu } from '../common/UserMenu';
import { getBusinessName } from '../../models/User';

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 68;

const navItems = [
  { label: 'Catalogs', icon: <StorefrontIcon />, path: '/business/catalogs' },
  { label: 'Analytics', icon: <BarChartIcon />, path: '/business/analytics' },
  { label: 'Orders', icon: <ReceiptLongIcon />, path: '/business/orders', disabled: true },
];

const BusinessLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const isActive = (path: string) => location.pathname.startsWith(path);

  // ── Drawer contents ──
  const isExpanded = isMobile || !collapsed; // mobile drawer is always expanded

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Brand — clickable to home */}
      <Box
        onClick={() => navigate('/')}
        sx={{
          p: isExpanded ? 2.5 : 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isExpanded ? 'flex-start' : 'center',
          gap: isExpanded ? 1.5 : 0,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': { opacity: 0.8 },
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Aakaar"
          sx={{ width: 40, height: 40, flexShrink: 0 }}
        />
        {isExpanded && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.5px' }}>
              Aakaar
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Business Portal
            </Typography>
          </Box>
        )}
      </Box>

      <Divider />

      {/* Navigation */}
      <List sx={{ flex: 1, px: isExpanded ? 1 : 0.5, pt: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
            <Tooltip title={!isExpanded ? item.label : (item.disabled ? 'Coming Soon' : '')} placement="right">
              <span style={{ width: '100%' }}>
                <ListItemButton
                  selected={isActive(item.path)}
                  disabled={item.disabled}
                  onClick={() => {
                    if (!item.disabled) {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    justifyContent: isExpanded ? 'flex-start' : 'center',
                    px: isExpanded ? 2 : 1.5,
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main}14`,
                      color: theme.palette.primary.main,
                      '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: `${theme.palette.primary.main}20`,
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: isExpanded ? 40 : 'unset', justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                  {isExpanded && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: isActive(item.path) ? 600 : 400 }}
                    />
                  )}
                  {isExpanded && item.disabled && (
                    <Chip
                      label="Soon"
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  )}
                </ListItemButton>
              </span>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      {/* Collapse / Expand toggle (desktop only) */}
      {!isMobile && (
        <>
          <Divider />
          <Box sx={{ p: 1, display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end' }}>
            <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
              <IconButton onClick={() => setCollapsed((prev) => !prev)} size="small">
                {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sidebar */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
            flexShrink: 0,
            transition: 'width 0.25s ease',
            '& .MuiDrawer-paper': {
              width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: 'width 0.25s ease',
              overflowX: 'hidden',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar — uses same pattern as CatalogHeader AppBar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Toolbar sx={{ py: 1 }}>
            {isMobile && (
              <IconButton edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {userProfile ? getBusinessName(userProfile) : ''}
              </Typography>
            </Box>
            <UserMenu />
          </Toolbar>
        </AppBar>

        {/* Page content — uses same container padding as CatalogView */}
        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 } }}>
          <Suspense
            fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                <CircularProgress />
              </Box>
            }
          >
            <ToastProvider>
              <Outlet />
            </ToastProvider>
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
};

export default BusinessLayout;
